import { useState, useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { getMsUntilTime } from '../services/ConfigService';

function formatTime(ms) {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

/**
 * @param {number} targetHour   - Hora objetivo en Europe/Madrid (0-23)
 * @param {number} targetMinute - Minuto objetivo en Europe/Madrid (0-59)
 * @param {function} onExpire   - Callback cuando llega a 0
 */
export default function CountdownTimer({ targetHour = 22, targetMinute = 5, onExpire }) {
  const [remaining, setRemaining] = useState(() => getMsUntilTime(targetHour, targetMinute));
  const expiredRef = useRef(false);

  useEffect(() => {
    expiredRef.current = false;
    setRemaining(getMsUntilTime(targetHour, targetMinute));
  }, [targetHour, targetMinute]);

  useEffect(() => {
    if (remaining <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
      return;
    }

    const interval = setInterval(() => {
      const ms = getMsUntilTime(targetHour, targetMinute);
      setRemaining(ms);
      if (ms <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [onExpire, remaining, targetHour, targetMinute]);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        borderRadius: 3,
        px: 3,
        py: 1.5,
        my: 2,
      }}
    >
      <Typography
        variant="h3"
        fontWeight={700}
        color="primary"
        sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: 4 }}
      >
        {formatTime(remaining)}
      </Typography>
    </Box>
  );
}
